/**
 * PostHog Analytics Service
 *
 * Manages PostHog SDK lifecycle and provides methods for
 * tracking events, user identification, and session replay.
 *
 * Self-hosted PostHog instance: https://ph.worldofwow.com
 * Only enabled on production (worldofwow.com) and localhost.
 */

import posthog from "posthog-js";
import { inject } from "../../di";
import { watch } from "../../di/effects";
import type { IAuthState, ICurrentUser } from "../auth/types";
import type { IPostHogService, PostHogEventProperties, PostHogUserProperties } from "./types";

const POSTHOG_HOST = "https://ph.worldofwow.com";
const POSTHOG_API_KEY = "phc_TnNdRGG9yHICgaP1sXrAe0tQQ4wwcVkzN59HBu2M1Sm";
const WOW_ANONYMOUS_ID_KEY = "wowAnonymousId";

/**
 * Check if PostHog should be enabled in the current environment.
 * Only enabled on production and localhost.
 */
function shouldEnablePostHog(): boolean {
  const hostname = window.location.hostname;
  const isEnabledOnLocalhost: boolean = false;
  return (
    (isEnabledOnLocalhost && hostname === "localhost") ||
    hostname === "worldofwow.com" ||
    hostname.endsWith(".worldofwow.com")
  );
}

export class PostHogService implements IPostHogService {
  @inject<IAuthState>("authState")
  private authState!: IAuthState;

  private initialized = false;
  private cleanupAuthWatch?: () => void;

  isEnabled(): boolean {
    return shouldEnablePostHog();
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }

    if (!this.isEnabled()) {
      console.log("[PostHog] Disabled - not on production or localhost");
      return;
    }

    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: true,
      capture_pageview: true,
      capture_pageleave: true,
      persistence: "localStorage+cookie",
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",
      },
    });

    this.initialized = true;
    console.log("[PostHog] Initialized with host:", POSTHOG_HOST);

    // Register WOW anonymous ID for correlation with existing analytics
    const wowAnonymousId = this.getWowAnonymousId();
    if (wowAnonymousId) {
      this.registerSuperProperties({ wow_anonymous_id: wowAnonymousId });
    }

    // Check if user is already authenticated
    const currentUser = this.authState.currentUser.get();
    if (currentUser) {
      this.identifyUser(currentUser);
    }

    // Subscribe to auth changes
    this.setupAuthListeners();
  }

  capture(eventName: string, properties?: PostHogEventProperties): void {
    if (!this.initialized || !this.isEnabled()) {
      return;
    }

    posthog.capture(eventName, properties);
  }

  identify(userId: string, properties?: PostHogUserProperties): void {
    if (!this.initialized || !this.isEnabled()) {
      return;
    }

    posthog.identify(userId, properties);
  }

  reset(): void {
    if (!this.initialized || !this.isEnabled()) {
      return;
    }

    posthog.reset();
  }

  registerSuperProperties(properties: PostHogEventProperties): void {
    if (!this.initialized || !this.isEnabled()) {
      return;
    }

    posthog.register(properties);
  }

  getWowAnonymousId(): string | undefined {
    try {
      return localStorage.getItem(WOW_ANONYMOUS_ID_KEY) ?? undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Set up listeners for auth state changes.
   * Uses the watch utility for signal reactivity.
   */
  private setupAuthListeners(): void {
    let previousUserId: string | undefined;

    this.cleanupAuthWatch = watch(
      () => this.authState.currentUser.get(),
      (currentUser) => {
        const currentUserId = currentUser?.id;

        if (currentUserId && !previousUserId) {
          // User signed in
          this.identifyUser(currentUser);
        } else if (!currentUserId && previousUserId) {
          // User signed out
          this.reset();
          console.log("[PostHog] User reset on sign-out");
        }

        previousUserId = currentUserId;
      },
    );
  }

  /**
   * Identify a user with PostHog.
   */
  private identifyUser(user: ICurrentUser): void {
    const properties: PostHogUserProperties = {
      handle: user.handle,
      name: user.name,
      role: user.roles?.[0],
    };

    this.identify(user.id, properties);
    console.log("[PostHog] User identified:", user.id);
  }
}
